import { AuthenticationError, AuthErrorType, type AuthDevice, type AuthProvider } from '@/domain/provider/AuthProvider';
import Keycloak, { type KeycloakOnLoad } from 'keycloak-js';
import { AUTH_SERVER_URL } from '@/lib/utils/env';

const config: {
  onLoad: KeycloakOnLoad
  refreshIfWithinSeconds: number
  clientId: string
  realm: string
} = {
  onLoad: 'login-required',
  refreshIfWithinSeconds: 5,
  clientId: 'public',
  realm: 'slingshot'
}

let keycloak: Keycloak
if (!(Keycloak as any).didInitialize) {
  keycloak = new Keycloak({
    clientId: config.clientId,
    realm: config.realm,
    url: AUTH_SERVER_URL
  })
}

const accountUrl = (path: string): string =>
  `${AUTH_SERVER_URL.replace(/\/+$/, '')}/realms/${config.realm}/account/${path}`

export const KeycloakAuthProvider: AuthProvider = {
  authenticate: async (): Promise<boolean | AuthenticationError> => {


    // ------------------------------------------------- //
    //                                                   //
    //                                                   //
    //    Dev Bypass for Local Storage Token             //
    //                                                   //
    //                                                   //
    // ------------------------------------------------- //

    if (import.meta.env.DEV && localStorage.getItem('token')) {
      console.warn('🟡 Mock Auth Bypass: Token found in localStorage, skipping Keycloak init');
      (keycloak as any).token = localStorage.getItem('token');
      (keycloak as any).tokenParsed = {
        preferred_username: 'devuser',
        email: 'devuser@example.com',
        resource_access: { [config.clientId]: { roles: ['developer'] } }
      };
      return true;
    }

    /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */
    /*  Normal Keycloak Flow      */
    /* ~~~~~~~~~~~~~~~~~~~~~~~~~~ */

    return keycloak.init({
      onLoad: config.onLoad,
      checkLoginIframe: false, // breaks on mobile
      pkceMethod: 'S256',
      flow: 'standard'
    })
      .then(authenticated => {
        if (authenticated) {
          return authenticated
        } else {
          throw new AuthenticationError('Authentication failed', AuthErrorType.NOT_AUTHENTICATED)
        }
      })
      .catch(error => {
        throw new AuthenticationError(`Authentication failed ${error}`, AuthErrorType.UNKNOWN)
      })
  },

  logout: (): Promise<void> => {
    return keycloak.logout()
  },

  isAuthenticated: () => !!keycloak.token,

  token: (): string => {
    return keycloak.token ? keycloak.token : 'NO_TOKEN'
  },

  tokenDetails: (): { [k: string]: any } => {
    return keycloak.tokenParsed as { [k: string]: any }
  },

  userRoles: (): string[] | undefined => {
    return keycloak.resourceAccess ? [config.clientId] : undefined
  },

  updateToken: async (): Promise<boolean | void> => {
    return keycloak.updateToken(config.refreshIfWithinSeconds).catch(keycloak.login)
  },

  getAuthorizationHeaderValue: async (): Promise<string> => {
    await KeycloakAuthProvider.updateToken()
    const token = KeycloakAuthProvider.token()
    return `Bearer ${token}`
  },


  // ------------------------------------------------- //
  //                                                   //
  //                                                   //
  //    Session Devices                                //
  //                                                   //
  //                                                   //
  // ------------------------------------------------- //

  /* ·················································································
   ·                                                                              ·
   ·  Account REST answers 404, not 401, without the JSON Accept header.          ·
   ·                                                                              ·
   ················································································· */

  listDevices: async (): Promise<AuthDevice[]> => {
    const authorization = await KeycloakAuthProvider.getAuthorizationHeaderValue()
    if (authorization === 'Bearer NO_TOKEN') return []

    const response = await fetch(accountUrl('sessions/devices'), {
      headers: { Authorization: authorization, Accept: 'application/json' }
    })

    if (!response.ok) throw new Error(`Failed to load devices - ${response.status}`)

    const devices = await response.json() as AuthDevice[]

    return devices
      .map(device => ({
        ...device,
        sessions: device.sessions.filter(session => session.clients.some(client => client.clientId === config.clientId))
      }))
      .filter(device => device.sessions.length > 0)
  },

  signOutOtherDevices: async (): Promise<void> => {
    const authorization = await KeycloakAuthProvider.getAuthorizationHeaderValue()

    const response = await fetch(accountUrl('sessions?current=false'), {
      method: 'DELETE',
      headers: { Authorization: authorization, Accept: 'application/json' }
    })

    if (!response.ok) throw new Error(`Failed to sign out other devices - ${response.status}`)
  }
}
